import React from 'react'
import { createRoot } from 'react-dom/client'
import StudentTableRow from '../components/student-table-row'
import CreateUserModal from '../components/create-user-modal'
import { ApolloProvider, useMutation, useQuery } from '@apollo/client'
import client from '../client'
import fetchStudents from '../queries/students'
import createSection from '../mutations/create-section'
import updateSection from '../mutations/update-section'
import addSectionToProject from '../mutations/add-section-to-project'
import * as R from 'ramda'
import Fuse from 'fuse.js'
import Toggle from 'react-toggle'

function SectionForm(props) {
  const [name, setName] = React.useState(props.name)
  const [studentIds, setStudentIds] = React.useState(props.studentIds || [])
  const [originalStudentIds, setOriginalStudentIds] = React.useState(
    props.studentIds || []
  )
  const [searchText, setSearchText] = React.useState('')
  const [creatingUser, setCreatingUser] = React.useState(false)
  const [addStudentsNow, setAddStudentsNow] = React.useState(true)
  const [toggleMessage, setToggleMessage] = React.useState(
    'Add students manually'
  )

  const { error, loading, data } = useQuery(fetchStudents, {
    variables: { groupId: props.groupId },
  })

  const [addSectionToProjectMutation] = useMutation(addSectionToProject)
  const [createSectionMutation] = useMutation(createSection)
  const [updateSectionMutation] = useMutation(updateSection)

  if (loading) return <div>Loading...</div>
  if (error) return <div>Error :(</div>

  const openCreateUserModal = () => {
    setCreatingUser(true)
  }

  const closeCreateUserModal = () => {
    data.refetch()

    setCreatingUser(false)
  }

  const afterSubmit = (resp) => {
    const hasProjectId = window.location.search.match(/\?project_id=(\d+)/)
    if (hasProjectId) {
      const { data } = resp
      const id = hasProjectId[1]
      const sectionId = data.createSection
        ? data.createSection && data.createSection.id
        : data.updateSection && data.updateSection.id

      addSectionToProjectMutation({
        variables: { id, sectionId },
      }).then(() => {
        window.location.href = `/projects/${id}/edit`
      })
    } else {
      window.history.back()
    }
  }

  const handleSubmit = (e) => {
    e.preventDefault()

    const { groupId, id } = props

    if (id) {
      updateSectionMutation({
        variables: { id, name, studentIds },
      }).then(afterSubmit)
    } else {
      createSectionMutation({
        variables: { groupId, name, studentIds },
      }).then(afterSubmit)
    }
  }

  const handleChange = (e) => {
    setName(e.target.value)
  }

  const handleSearch = (e) => {
    setSearchText(e.target.value)
  }

  const filterStudents = (student) => {
    const { id, studentIds } = props

    // Exclude archived students from new sections
    if (!id && !student.active) {
      return false
    }

    // Exclude archived students from existing sections
    // which they were not part of. Uses props.studentIds
    // to ensure that archived students that used to be
    // part of the section will still be available until
    // you resave the section.
    if (
      typeof id === 'string' &&
      !student.active &&
      !studentIds.includes(student.id)
    ) {
      return false
    }

    return true
  }

  const sortStudentsBySearch = (students) => {
    if (searchText === '') {
      return students
    }

    const fuseOptions = {
      shouldSort: true,
      tokenize: true,
      threshold: 1,
      location: 0,
      distance: 100,
      maxPatternLength: 32,
      minMatchCharLength: 1,
      keys: ['name', 'email'],
    }

    const fuse = new Fuse(students, fuseOptions)

    return fuse.search(searchText)
  }

  const isOriginallySelected = (student) => {
    return originalStudentIds.includes(student.id)
  }

  const isSelected = (student) => {
    return studentIds.includes(student.id)
  }

  const alreadyAssigned = (student) => {
    const { id } = props
    const studentIds =
      data.group &&
      R.flatten(
        data.group.sections
          .filter((s) => s.id !== id)
          .map((s) => s.students.map((st) => st.id))
      )

    return studentIds.includes(student.id)
  }

  const toggleSelected = (student) => {
    if (isSelected(student)) {
      setStudentIds(R.without([student.id], studentIds))
    } else {
      setStudentIds(R.append(student.id, studentIds))
    }
  }

  const sortStudents = (students) => {
    students = students.filter(filterStudents)

    if (searchText.length > 0) {
      return sortStudentsBySearch(students)
    } else {
      let sort = R.sortWith([
        R.ascend((student) => alreadyAssigned(student)),
        R.descend((student) => isOriginallySelected(student)),
        R.ascend(R.prop('name')),
      ])

      return sort(students)
    }
  }

  const handleAddStudentsNowChange = (addStudentsNow) => {
    const addStudentsNowFlag = !addStudentsNow
    let toggleMessage
    if (addStudentsNowFlag) {
      toggleMessage = 'Add students manually'
    } else {
      toggleMessage = 'Generate Class Code'
    }
    setAddStudentsNow(addStudentsNowFlag)
    setToggleMessage(toggleMessage)
  }
  const { owned, readOnly, userRole } = props

  return (
    <div className="section-form">
      <CreateUserModal isOpen={creatingUser} onClose={closeCreateUserModal} />
      <form onSubmit={handleSubmit}>
        <div className="header">
          <div className="field floating section-name">
            <input
              type="text"
              value={name}
              onChange={handleChange}
              required
              disabled={!!readOnly}
              className={readOnly ? 'valid' : ''}
            />
            <label>Section Name</label>
            <div className="highlight" />
          </div>
          {addStudentsNow && (
            <div className="search">
              <i className="material-icons">search</i>
              <input
                type="text"
                value={searchText}
                onChange={handleSearch}
                placeholder="Search Students..."
              />
            </div>
          )}
          {userRole === 'Teacher' && owned === 'true' && (
            <div className="section-options">
              <label>
                <Toggle
                  defaultChecked={addStudentsNow}
                  icons={false}
                  onChange={() => handleAddStudentsNowChange(addStudentsNow)}
                />
                <div>{toggleMessage}</div>
              </label>
              {!addStudentsNow && (
                <div>
                  <p>
                    A unique Class Code will be created for this Section.
                    Provide the Class Code to your students. They will paste it
                    into Refleq to join the Section.
                  </p>
                  <p>
                    Note this process relies on the students to self-enroll.
                  </p>
                  <img src="/images/enter_class_code.png" />
                </div>
              )}
            </div>
          )}
        </div>
        {addStudentsNow && (
          <table className="fancy">
            <thead>
              <tr>
                <th>
                  <div>{readOnly ? 'Added' : 'Add'}</div>
                </th>
                <th>
                  <div>Name</div>
                </th>
                {!readOnly && (
                  <th>
                    <div>Email</div>
                  </th>
                )}
                <th>
                  <div>Graduation Year</div>
                </th>
              </tr>
            </thead>
            <tbody>
              {data.students &&
                sortStudents(data.students).map((s) => (
                  <StudentTableRow
                    data={s}
                    disabled={alreadyAssigned(s)}
                    checked={isSelected(s)}
                    onClick={() => (readOnly ? null : toggleSelected(s))}
                    readOnly={readOnly}
                  />
                ))}
            </tbody>
          </table>
        )}
        {!readOnly && (
          <footer className="section-footer">
            <div className="container">
              <div className="content">
                {addStudentsNow && userRole === 'Teacher' && (
                  <span className="add-user">
                    Don't see the students I need? Contact my organization's
                    ReflEQ administrator to import students, or add them myself{' '}
                    <a onClick={openCreateUserModal}>HERE</a>.
                  </span>
                )}
                {addStudentsNow && (
                  <span className="count">{studentIds.length} selected</span>
                )}
                <button>Save Section And Exit</button>
              </div>
            </div>
          </footer>
        )}
      </form>
    </div>
  )
}

const sectionForm = document.getElementById('section-form')
if (sectionForm) {
  const readOnly = sectionForm.getAttribute('data-read-only')
  const groupId = sectionForm.getAttribute('data-group-id')
  const id = sectionForm.getAttribute('data-id')
  const organizationId = sectionForm.getAttribute('data-organization-id')
  const name = sectionForm.getAttribute('data-name')
  const studentIds = JSON.parse(sectionForm.getAttribute('data-student-ids'))
  const userRole = sectionForm.getAttribute('data-user-role')
  const owned = sectionForm.getAttribute('data-owned')
  const props = {
    groupId,
    id,
    name,
    studentIds,
    readOnly,
    organizationId,
    owned,
    userRole,
  }

  const root = createRoot(sectionForm)

  root.render(
    <ApolloProvider client={client}>
      <SectionForm {...props} />
    </ApolloProvider>
  )
}
